/**
 * TaskModal — create / edit a task. When editing, also shows comments and
 * time entries with quick-log. Used by ProjectDetail and GlobalTasks.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Clock, MessageSquare, Trash2, Plus, Send, Paperclip, Play, Download } from 'lucide-react';
import { wsApi, assetUrl } from './wsApi';
import { TASK_STATUS, PRIORITY, initials, avatarColor, fmtDate, fmtDuration, toLocalInputValue, localInputToIso, localDateStr } from './wsConstants';
import { MemberCombobox } from './wsUi';
import { selectChevron } from '../../components/em/ui';

const EMPTY = {
  title: '', description: '', status: 'todo', priority: 'medium',
  due_date: '', project_id: '',
  // One list for everyone on the task. Each entry carries the bucket they sit
  // in (member_role) and their standing (role) — mirrors ws_task_members.
  people: [],
};

// Which bucket a person is in.
const TASK_BUCKETS = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'collaborator', label: 'Collaborator' },
  { value: 'watcher', label: 'Watcher' },
];

// Broadcast that task-derived data changed (logged time, comment/file counts).
// The Time/Comments/Files tabs mutate data the *parent* renders — project
// "Hours logged", the per-row duration chip — but they can't use onSaved,
// which closes the modal. Pages listen for this and refetch in place.
// TimerWidget fires the same event when a live timer stops.
export const notifyWorkspaceDataChanged = () =>
  window.dispatchEvent(new Event('workspaceDataChanged'));

export default function TaskModal({ taskId, presetProjectId, projects = [], users = [], onClose, onSaved }) {
  const editing = !!taskId;
  const [form, setForm] = useState({ ...EMPTY, project_id: presetProjectId || '' });
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('details');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Sub-tabs hand back the refetched task. Keep the modal in sync *and* tell
  // the page, so stats like "Hours logged" move without a manual refresh.
  const applyTask = useCallback((updated) => {
    setTask(updated);
    notifyWorkspaceDataChanged();
  }, []);

  useEffect(() => {
    if (!editing) return;
    wsApi.getTask(taskId)
      .then((t) => {
        setTask(t);
        setForm({
          title: t.title || '',
          description: t.description || '',
          status: t.status,
          priority: t.priority,
          due_date: t.due_date ? String(t.due_date).slice(0, 10) : '',
          project_id: t.project_id || '',
          people: (t.members || []).map((m) => ({
            user_id: Number(m.user_id),
            member_role: m.member_role || 'assignee',
            role: m.role || 'member',
          })),
        });
      })
      .catch(() => toast.error('Could not load task'))
      .finally(() => setLoading(false));
  }, [taskId, editing]);

  const save = async () => {
    if (!form.title.trim()) return toast.error('Task title is required');
    setSaving(true);
    try {
      const body = { ...form, project_id: form.project_id || null, due_date: form.due_date || null };
      if (editing) await wsApi.updateTask(taskId, body);
      else await wsApi.createTask(body);
      toast.success(editing ? 'Task updated' : 'Task created');
      onSaved();
    } catch {
      toast.error('Could not save task');
    } finally {
      setSaving(false);
    }
  };

  // Add on first click, remove on second — matches MemberCombobox's toggle
  // contract so clicking a name in the dropdown behaves as expected.
  const togglePerson = (uid) => {
    const id = Number(uid);
    setForm((f) => {
      const has = f.people.some((p) => p.user_id === id);
      return {
        ...f,
        people: has
          ? f.people.filter((p) => p.user_id !== id)
          : [...f.people, { user_id: id, member_role: 'assignee', role: 'member' }],
      };
    });
  };

  const removePersonAt = (idx) =>
    setForm((f) => ({ ...f, people: f.people.filter((_, i) => i !== idx) }));

  // Rows are addressed by index, not user_id: `ws_task_members` is unique on
  // (task_id, user_id, member_role), so the same person legitimately appears in
  // more than one bucket and user_id alone doesn't identify a row.
  const setPersonField = (idx, key, value) =>
    setForm((f) => {
      const next = f.people.map((p, i) => (i === idx ? { ...p, [key]: value } : p));
      // Changing a bucket can collide with another row for the same person —
      // collapse the pair rather than writing a duplicate the DB would reject.
      const edited = next[idx];
      return {
        ...f,
        people: next.filter(
          (p, i) =>
            i === idx ||
            !(p.user_id === edited.user_id && p.member_role === edited.member_role)
        ),
      };
    });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{editing ? 'Task Details' : 'New Task'}</h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : (
          <>
            {editing && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--em-border)' }}>
                {[
                  { k: 'details', label: 'Details' },
                  { k: 'comments', label: `Comments${task?.comment_count ? ` (${task.comment_count})` : ''}`, Icon: MessageSquare },
                  { k: 'files', label: `Files${task?.attachments?.length ? ` (${task.attachments.length})` : ''}`, Icon: Paperclip },
                  { k: 'time', label: 'Time', Icon: Clock },
                ].map((t) => (
                  <button key={t.k} onClick={() => setTab(t.k)} style={{
                    padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: tab === t.k ? 600 : 500,
                    color: tab === t.k ? '#f54b01' : '#6b7280',
                    borderBottom: `2px solid ${tab === t.k ? '#f54b01' : 'transparent'}`, marginBottom: -1,
                  }}>{t.label}</button>
                ))}
              </div>
            )}

            {tab === 'details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="Title *">
                  <input style={input} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="What needs to be done?" autoFocus={!editing} />
                </Field>
                <Field label="Description">
                  <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={form.description} onChange={(e) => set('description', e.target.value)} />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Project">
                    <select style={input} value={form.project_id || ''} onChange={(e) => set('project_id', e.target.value)}>
                      <option value="">— Quick task (no project) —</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Due date">
                    <input type="date" style={input} value={form.due_date} onChange={(e) => set('due_date', e.target.value)} />
                  </Field>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Status">
                    <select style={input} value={form.status} onChange={(e) => set('status', e.target.value)}>
                      {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Priority">
                    <select style={input} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                      {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                </div>

                {/* People — one searchable combobox to add, then a row per
                    person with their bucket + role (same pattern as the
                    project "Manage people" modal). */}
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)', marginBottom: 5 }}>Add people</div>
                  <MemberCombobox
                    users={users.filter((u) => !form.people.some((p) => p.user_id === Number(u.id)))}
                    selected={[]}
                    onToggle={togglePerson}
                    placeholder="Search people to add…"
                  />
                </div>

                {form.people.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)', marginBottom: 5 }}>
                      On this task ({form.people.length})
                    </div>
                    <div style={{ border: '1px solid var(--em-border)', borderRadius: 8, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                      {form.people.map((p, i) => {
                        const u = users.find((x) => Number(x.id) === p.user_id);
                        return (
                          <div key={`${p.user_id}:${p.member_role}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderTop: i ? '1px solid var(--em-divider)' : 'none' }}>
                            <span style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: avatarColor(u?.username || ''), color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {initials(u?.username || '?')}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--em-fg1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {u?.username || `User #${p.user_id}`}
                            </span>
                            <select
                              value={p.member_role}
                              onChange={(e) => setPersonField(i, 'member_role', e.target.value)}
                              style={{ ...miniSelect, width: 116 }}
                            >
                              {TASK_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                            <select
                              value={p.role}
                              onChange={(e) => setPersonField(i, 'role', e.target.value)}
                              style={{ ...miniSelect, width: 96 }}
                            >
                              <option value="manager">Manager</option>
                              <option value="member">Member</option>
                            </select>
                            <button type="button" title="Remove" onClick={() => removePersonAt(i)}
                              style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--em-danger)', display: 'flex', padding: 4 }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'comments' && editing && (
              <CommentsTab task={task} onChange={applyTask} />
            )}

            {tab === 'files' && editing && (
              <FilesTab task={task} onChange={applyTask} />
            )}

            {tab === 'time' && editing && (
              <TimeTab task={task} onChange={applyTask} />
            )}

            {tab === 'details' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                <button onClick={onClose} style={ghostBtn}>Cancel</button>
                <button onClick={save} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Task'}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Member picker (chips) ───────────────────────────────────────────────────

// ── Files tab ───────────────────────────────────────────────────────────────
function FilesTab({ task, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await wsApi.uploadAttachment(task.id, file);
      onChange(updated);
      toast.success('File uploaded');
    } catch (err) {
      // Prefer the server's actual reason (too large / unsupported type) over
      // a guess. Two error shapes exist: { error: "text" } from controllers,
      // { error: { code, message } } from the central handler.
      const e = err?.response?.data?.error;
      const msg = typeof e === 'string' ? e : e?.message;
      toast.error(msg || 'Upload failed — check the file type and that it is 10 MB or smaller.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const del = async (id) => {
    try {
      await wsApi.deleteAttachment(id);
      onChange(await wsApi.getTask(task.id));
    } catch {
      toast.error('Could not delete file');
    }
  };

  return (
    <div>
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '22px 0', border: '1.5px dashed #d1d5db', borderRadius: 10,
        cursor: 'pointer', color: 'var(--em-fg5)', fontSize: 13, marginBottom: 14,
      }}>
        <Paperclip size={20} color="#9ca3af" />
        {uploading ? 'Uploading…' : <span><span style={{ color: '#f54b01', fontWeight: 600 }}>Click to upload</span> a file</span>}
        <span style={{ fontSize: 11, color: '#9ca3af' }}>PDF, images, docs, sheets — up to 10MB</span>
        <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => upload(e.target.files?.[0])} />
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
        {(task.attachments || []).length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 14 }}>No files attached.</p>
        )}
        {(task.attachments || []).map((a) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--em-bg-head)', borderRadius: 8 }}>
            <Paperclip size={14} color="#9ca3af" />
            <a href={assetUrl(a.file_url)} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#374151', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {a.original_name || 'file'}
            </a>
            <a href={assetUrl(a.file_url)} download target="_blank" rel="noreferrer" style={{ display: 'flex', color: '#9ca3af' }}><Download size={14} /></a>
            <button onClick={() => del(a.id)} style={iconBtn}><Trash2 size={13} color="#9ca3af" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Comments tab ────────────────────────────────────────────────────────────
function CommentsTab({ task, onChange }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const updated = await wsApi.addComment(task.id, text.trim());
      onChange(updated);
      setText('');
    } catch {
      toast.error('Could not post comment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto', marginBottom: 14 }}>
        {(task.comments || []).length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 20 }}>No comments yet.</p>
        )}
        {(task.comments || []).map((c) => (
          <div key={c.id} style={{ display: 'flex', gap: 10 }}>
            <span style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: avatarColor(c.username), color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {initials(c.username)}
            </span>
            <div style={{ flex: 1, background: 'var(--em-bg-head)', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#111827' }}>{c.username}
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 11 }}>{fmtDate(c.created_at)}</span>
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.comment}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={input} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…"
          onKeyDown={(e) => e.key === 'Enter' && post()} />
        <button onClick={post} disabled={posting} style={{ ...primaryBtn, padding: '9px 14px' }}><Send size={15} /></button>
      </div>
    </div>
  );
}

// ── Time tab ────────────────────────────────────────────────────────────────
function TimeTab({ task, onChange }) {
  const [mode, setMode] = useState('duration'); // 'duration' | 'range'
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [startAt, setStartAt] = useState(() => toLocalInputValue());
  const [endAt, setEndAt] = useState(() => toLocalInputValue());
  const [desc, setDesc] = useState('');
  const [billable, setBillable] = useState(true);
  const [saving, setSaving] = useState(false);

  // logged_minutes / duration_minutes arrive as numbers here, but coerce
  // defensively so the total never string-concatenates.
  const totalLogged = (task.time_entries || []).reduce((s, e) => s + (Number(e.duration_minutes) || 0), 0);

  // Live preview of the range duration (minutes) — null if invalid.
  const rangeMins = (() => {
    if (mode !== 'range' || !startAt || !endAt) return null;
    const ms = new Date(endAt).getTime() - new Date(startAt).getTime();
    return ms > 0 ? Math.round(ms / 60000) : null;
  })();

  const log = async () => {
    let body = { task_id: task.id, project_id: task.project_id || null, description: desc, billable: billable ? 1 : 0 };
    if (mode === 'range') {
      if (!startAt || !endAt) return toast.error('Pick a start and end time');
      if (rangeMins == null) return toast.error('End time must be after start time');
      if (rangeMins > 1440) return toast.error("A single entry can't exceed 24 hours");
      body = {
        ...body,
        started_at: localInputToIso(startAt),
        ended_at: localInputToIso(endAt),
        entry_date: localDateStr(new Date(startAt)),
      };
    } else {
      const mins = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
      if (mins <= 0) return toast.error('Enter a duration');
      body = { ...body, duration_minutes: mins };
    }
    setSaving(true);
    try {
      await wsApi.addTimeEntry(body);
      const updated = await wsApi.getTask(task.id);
      onChange(updated);
      setHours(''); setMinutes(''); setDesc('');
      setStartAt(toLocalInputValue()); setEndAt(toLocalInputValue());
      toast.success('Time logged');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not log time');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await wsApi.deleteTimeEntry(id);
      onChange(await wsApi.getTask(task.id));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete entry');
    }
  };

  const startTimer = async () => {
    try {
      await wsApi.startTimer({ task_id: task.id, project_id: task.project_id || null, description: task.title });
      window.dispatchEvent(new Event('workspaceTimerChanged'));
      toast.success('Timer started — see the header');
    } catch {
      toast.error('Could not start timer');
    }
  };

  return (
    <div>
      <button onClick={startTimer} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%',
        background: '#fff3ec', color: '#c2410c', border: '1px solid #fdba74', borderRadius: 9,
        padding: '10px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14,
      }}>
        <Play size={14} fill="#c2410c" /> Start live timer for this task
      </button>

      <div style={{ background: 'var(--em-bg-head)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>Log time manually</div>
          {/* Mode toggle: quick duration vs. explicit start/end time. */}
          <div style={{ display: 'flex', background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 8, padding: 2 }}>
            {[['duration', 'Duration'], ['range', 'Start & end']].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 600,
                  background: mode === m ? '#f54b01' : 'transparent',
                  color: mode === m ? '#fff' : '#6b7280',
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {mode === 'duration' ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="number" min="0" style={{ ...input, width: 70 }} placeholder="hrs" value={hours} onChange={(e) => setHours(e.target.value)} />
            <input type="number" min="0" max="59" style={{ ...input, width: 70 }} placeholder="min" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            <input style={{ ...input, flex: 1, minWidth: 120 }} placeholder="What did you work on?" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#6b7280', flex: 1, minWidth: 150 }}>
                Start
                <input type="datetime-local" style={input} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#6b7280', flex: 1, minWidth: 150 }}>
                End
                <input type="datetime-local" style={input} value={endAt} onChange={(e) => setEndAt(e.target.value)} />
              </label>
            </div>
            <input style={input} placeholder="What did you work on?" value={desc} onChange={(e) => setDesc(e.target.value)} />
            <div style={{ fontSize: 11.5, color: rangeMins == null ? '#dc2626' : '#6b7280' }}>
              {rangeMins == null ? 'End time must be after start time' : `Duration: ${fmtDuration(rangeMins)}`}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable
          </label>
          <button onClick={log} disabled={saving} style={{ ...primaryBtn, padding: '7px 14px' }}><Plus size={14} /> Log</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--em-fg5)', marginBottom: 8 }}>
        <span>Entries</span>
        <span><strong>{fmtDuration(totalLogged)}</strong> total</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
        {(task.time_entries || []).length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 14 }}>No time logged yet.</p>
        )}
        {(task.time_entries || []).map((e) => (
          <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--em-bg-head)', borderRadius: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', width: 52 }}>{fmtDuration(e.duration_minutes)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.description || '—'}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{e.username} · {fmtDate(e.entry_date)}</div>
            </div>
            {!!e.billable && <span style={{ fontSize: 10, fontWeight: 600, color: '#065f46', background: '#d1fae5', padding: '2px 7px', borderRadius: 12 }}>Billable</span>}
            <button onClick={() => del(e.id)} style={iconBtn}><Trash2 size={13} color="#9ca3af" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── shared styles (mirrors ProjectsList) ────────────────────────────────────
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{label}</span>
      {children}
    </label>
  );
}
const input = { width: '100%', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 11px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
// Compact select used inside a people row (with the shared dropdown chevron).
const miniSelect = {
  flexShrink: 0, border: '1px solid var(--em-border)', borderRadius: 7,
  padding: '5px 8px', fontSize: 12, fontFamily: 'inherit',
  background: 'var(--em-bg-card)', color: 'var(--em-fg1)',
  ...selectChevron, paddingRight: 22,
};
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modal = { background: 'var(--em-bg-card)', borderRadius: 14, padding: '22px 24px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 7, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--em-bg-card)', color: '#374151', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
